"use client";

import React from "react";
import { CircleQuestionMark as HelpCircle, ShieldCheck, Ruler, Clock, FileCheck, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface FaqItem {
  id: string;
  icon: React.ElementType;
  question: string;
  answerParagraphs: string[];
  listItems?: string[];
}
const FAQ_ITEMS: FaqItem[] = [{
  id: "materials-durability",
  icon: ShieldCheck,
  question: "What deck materials hold up best in Brookhaven's humid climate?",
  answerParagraphs: ["In metro Atlanta and Brookhaven, high summer humidity, heavy spring downpours, and intense UV exposure accelerate organic decay and warping in traditional pressure-treated pine.", "We recommend capped composite or PVC decking (such as Trex Transcend or TimberTech AZEK) paired with kiln-dried or steel framing. While pressure-treated wood offers an economical upfront option, composite installations deliver superior return on investment through 25- to 50-year manufacturer warranties and zero seasonal staining."],
  listItems: ["Capped composite / PVC: Resists moisture pooling, mold, and splintering; requires simple soap-and-water cleaning.", "Pressure-treated southern pine: Budget-friendly initial cost, but requires annual power washing and sealing every 18-24 months.", "Hardwoods (Ipe, Cumaru): Naturally rot-resistant and elegant, requiring dedicated UV oil treatments."]
}, {
  id: "brookhaven-permits",
  icon: FileCheck,
  question: "Does the City of Brookhaven require building permits for deck remodeling?",
  answerParagraphs: ["Yes. The City of Brookhaven Community Development Department requires a residential building permit for all new decks, structural enlargements, second-story builds, and any rebuild that replaces structural framing, posts, or ledger boards.", "Brookhaven Elevation Renovations coordinates all local zoning verification, tree canopy protection compliance, engineered site plan submittals, and inspection scheduling directly with Brookhaven City Hall on Peachtree Road."],
  listItems: ["Cosmetic deck board swaps on sound, code-compliant frames may be expedited, but structural framing changes require stamped drawings.", "Zoning setbacks and impervious surface coverage limits (often restricted under Brookhaven municipal code) must be verified prior to ground disturbance."]
}, {
  id: "railing-code",
  icon: Ruler,
  question: "What are the specific railing and guardrail safety code requirements?",
  answerParagraphs: ["Under the International Residential Code (IRC) enforced across DeKalb County and Brookhaven, any deck walking surface situated more than 30 inches above adjacent finished grade requires a continuous protective guardrail.", "Key dimensions and safety standards our structural carpenters inspect and execute include:"],
  listItems: ["Guardrail height: Minimum 36 inches measured vertically from the finished decking surface.", "Baluster spacing: No opening can permit the passage of a 4-inch diameter sphere.", "Structural load tolerance: Railings and posts must withstand a concentrated lateral load of at least 200 pounds applied in any direction.", "Grip-rail handrails: Continuous graspable handrails are mandated for stairs with four or more risers."]
}, {
  id: "inspection-vs-rebuild",
  icon: HelpCircle,
  question: "Can my existing deck frame be resurfaced, or is a full rebuild necessary?",
  answerParagraphs: ["Resurfacing—replacing only the surface boards and railing while keeping the sub-frame—is viable only if the underlying structure satisfies current building codes and exhibits no rot.", "During our initial on-site evaluation in Brookhaven, we perform a multi-point structural integrity audit:"],
  listItems: ["Ledger board connection: Verifying structural tension-tie hardware, non-corrosive through-bolts, and intact counter-flashing preventing house rot.", "Joist and beam condition: Checking for fungal rot, framing sag, or spacing exceeding manufacturer spans (often 12-inch or 16-inch on-center for composites).", "Footing depth and stability: Ensuring concrete piers extend below Georgia's local frost line (12 to 18 inches minimum) with proper post-to-beam connectors."]
}, {
  id: "project-timeline",
  icon: Clock,
  question: "What is the typical timeline for a complete deck renovation project?",
  answerParagraphs: ["A standard deck remodeling project with Brookhaven Elevation Renovations spans between 3 to 7 weeks from contract signing to final completion, divided into two distinct phases:"],
  listItems: ["Pre-construction (2-3 weeks): Architectural measurements, 3D renderings, material selection, and City of Brookhaven permit review.", "Active demolition & construction (1-3 weeks): Demolition, footing pouring, structural framing, electrical rough-ins (for recessed lights or fans), decking installation, and railing assembly.", "Final inspection & turnover (3-5 days): Municipal building sign-off, punch-list walkthrough, and site restoration."]
}];
export default function DeckRemodelingFaq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section id="deckremodelingfaq" data-nav="light" ref={ref} className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden transition-opacity duration-700" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Context, Imagery, and Contact Prompt */}
          <div className="lg:col-span-5 flex flex-col space-y-8">
            <div>
              <span className="text-primary font-medium text-xs tracking-wider uppercase">
                Guidance & Code Clarity
              </span>
              <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-foreground leading-[1.15]">
                Brookhaven deck remodeling answers.
              </h2>
              <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
                From City of Brookhaven municipal permitting rules and DeKalb County building codes to humid-climate composite materials, here is what homeowners need to know before starting.
              </p>
            </div>

            {/* Visual Featured Work Frame */}
            <div className="relative rounded-2xl overflow-hidden border border-border bg-card shadow-sm group">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_custom_deck_renovation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_custom_deck_renovation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_custom_deck_renovation.png-wvc-sizes" alt="Finished composite deck and outdoor living renovation in Brookhaven, Georgia" loading="lazy" decoding="async" className="w-full h-64 sm:h-72 object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="p-5 bg-card border-t border-border">
                <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                  Local Experience
                </p>
                <h3 className="text-base font-semibold text-card-foreground mt-1">
                  Built to endure Georgia weather
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Every Brookhaven Elevation Renovations deck is engineered for water drainage, structural longevity, and code compliance.
                </p>
              </div>
            </div>

            {/* Direct Consultation Box */}
            <div className="p-6 rounded-xl bg-accent border border-border">
              <h4 className="font-serif text-lg font-semibold text-accent-foreground">
                Have a question about your property?
              </h4>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                Schedule an on-site structural evaluation with our remodeling team to examine your existing ledger, joists, and elevation.
              </p>
              <div className="mt-5">
                <Button asChild className="w-full sm:w-auto font-medium">
                  <Link to="/ContactRequestAnEstimatePage">
                    Request an estimate
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column: Accordion Questions */}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible defaultValue="materials-durability" className="w-full space-y-4">
              {FAQ_ITEMS.map((item, index) => {
              const IconComponent = item.icon;
              return <AccordionItem key={item.id} value={item.id} data-index={index} className="border border-border rounded-xl px-5 sm:px-6 py-1 bg-card shadow-xs transition-all data-[state=open]:shadow-md data-[state=open]:border-primary/40">
                    <AccordionTrigger className="hover:no-underline py-5 text-left group">
                      <div className="flex items-center gap-3.5 pr-2">
                        <span className="flex items-center justify-center size-9 rounded-lg bg-muted text-foreground shrink-0 group-data-[state=open]:bg-primary group-data-[state=open]:text-primary-foreground transition-colors">
                          <IconComponent className="size-4" />
                        </span>
                        <span className="text-base sm:text-lg font-serif font-semibold text-card-foreground group-hover:text-primary transition-colors">
                          {item.question}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-1 pb-6 text-card-foreground">
                      <div className="space-y-4 text-sm sm:text-base leading-relaxed pl-1 sm:pl-12">
                        {item.answerParagraphs.map((paragraph, pIndex) => <p key={pIndex} className="text-muted-foreground">
                            {paragraph}
                          </p>)}

                        {item.listItems && item.listItems.length > 0 && <ul className="space-y-2 mt-3 pt-2 border-t border-border/60">
                            {item.listItems.map((bullet, bIndex) => <li key={bIndex} className="flex items-start text-sm text-muted-foreground">
                                <span className="mr-2 text-primary font-bold leading-tight select-none">
                                  &bull;
                                </span>
                                <span>{bullet}</span>
                              </li>)}
                          </ul>}
                      </div>
                    </AccordionContent>
                  </AccordionItem>;
            })}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}